/**
 * photoDedup — client-side photo deduplication utilities.
 *
 * Two complementary layers:
 *
 * 1. Content hash (SHA-256)
 *    Exact dedup within the current browser session. The hash is computed
 *    from the raw file bytes via the Web Crypto API, so two identically-
 *    named files with different content will NOT match, and two differently-
 *    named files with the same content WILL match.
 *
 * 2. Byte-size + filename heuristic
 *    Cross-session dedup against already-persisted proofs. Because we don't
 *    store content hashes in the DB today, we use (byte_size, lower-cased
 *    file_name) as a strong-but-not-perfect fingerprint. This catches the
 *    overwhelmingly common case of a reviewer re-uploading the exact same
 *    file they uploaded in a previous session.
 *
 * Usage:
 *   const hash = await computeFileHash(file);
 *   if (isSessionDuplicate(hash)) { toast.warning(...); return; }
 *   const existing = findExistingDuplicate(file, proofs);
 *   if (existing) { toast.warning(...); return; }
 *   // ... perform upload ...
 *   registerUploadHash(hash);
 */

/** Module-level Set that persists for the lifetime of the browser tab. */
const sessionHashes = new Set<string>();

/**
 * Compute a hex-encoded SHA-256 digest of the file's raw bytes.
 * Returns an empty string if the Web Crypto API is unavailable.
 */
export async function computeFileHash(file: File): Promise<string> {
  try {
    const buffer = await file.arrayBuffer();
    const hashBuffer = await crypto.subtle.digest("SHA-256", buffer);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    // Crypto API unavailable (non-secure context, etc.) — skip hash dedup.
    return "";
  }
}

/**
 * Returns true if this exact file content has already been uploaded during
 * the current browser session. Pass the result of `computeFileHash`.
 * An empty hash (crypto unavailable) is never considered a duplicate.
 */
export function isSessionDuplicate(hash: string): boolean {
  if (!hash) return false;
  return sessionHashes.has(hash);
}

/**
 * Register a hash as uploaded. Call this after a successful upload so future
 * calls to `isSessionDuplicate` block re-uploads in the same session.
 */
export function registerUploadHash(hash: string): void {
  if (hash) sessionHashes.add(hash);
}

/** Minimal shape required from existing proofs for the heuristic check. */
export interface DedupProofCandidate {
  file_name: string | null;
  byte_size: number | null;
}

/**
 * Returns the first existing proof that matches the file by both byte size
 * and file name (case-insensitive). Returns null if no match is found or if
 * the file has no meaningful size (e.g. 0 bytes).
 *
 * Intended for cross-session dedup against already-persisted proofs where
 * content hashes are not stored in the DB.
 */
export function findExistingDuplicate(
  file: File,
  existingProofs: DedupProofCandidate[],
): DedupProofCandidate | null {
  if (!file.size) return null;
  return (
    existingProofs.find(
      (p) =>
        p.byte_size === file.size &&
        p.file_name != null &&
        p.file_name.toLowerCase() === file.name.toLowerCase(),
    ) ?? null
  );
}
